import type { LateArrivalRule } from '../types/lateArrivalConfiguration';

// Same semantics as the backend src/utils/late-arrival-rules.js and
// sp_att_apply_late_arrival_deduction.

type RuleRange = Pick<LateArrivalRule, 'occurrenceFrom' | 'occurrenceTo'>;

function roundDays(value: number): number {
  return Math.round(value * 100) / 100;
}

export function isLateArrival(
  lateMinutes: number | null | undefined,
  thresholdMinutes: number | null | undefined,
): boolean {
  if (typeof lateMinutes !== 'number' || typeof thresholdMinutes !== 'number') {
    return false;
  }
  return lateMinutes > thresholdMinutes;
}

export function sortRules<T extends RuleRange>(rules: T[]): T[] {
  return [...rules].sort((left, right) => left.occurrenceFrom - right.occurrenceFrom);
}

export function findRuleForOccurrence<T extends RuleRange>(
  rules: T[],
  occurrence: number,
): T | null {
  if (!Number.isInteger(occurrence) || occurrence < 1) return null;
  return (
    sortRules(rules)
      .reverse()
      .find(
        rule =>
          rule.occurrenceFrom <= occurrence &&
          (rule.occurrenceTo === null || rule.occurrenceTo >= occurrence),
      ) ?? null
  );
}

export function splitDeduction(
  ruleDays: number,
  availableDays: number,
  lopOnInsufficientBalance: boolean,
  hasBalance = true,
): { leaveDays: number; lopDays: number } {
  const days = roundDays(Math.max(0, ruleDays || 0));
  if (!hasBalance) return { leaveDays: 0, lopDays: days };
  if (!lopOnInsufficientBalance) return { leaveDays: days, lopDays: 0 };
  const leaveDays = roundDays(Math.min(Math.max(0, availableDays || 0), days));
  return { leaveDays, lopDays: roundDays(days - leaveDays) };
}

/** Returns an error message, or null when the rule set is valid. */
export function ruleSetError(rules: RuleRange[]): string | null {
  const sorted = sortRules(rules);
  for (let index = 0; index < sorted.length; index += 1) {
    const rule = sorted[index];
    if (index === 0 && rule.occurrenceFrom !== 1) {
      return 'The first late arrival rule must start at occurrence 1';
    }
    if (index > 0) {
      const previousTo = sorted[index - 1].occurrenceTo;
      if (previousTo === null || rule.occurrenceFrom !== previousTo + 1) {
        return 'Late arrival occurrences must continue without gaps or overlaps';
      }
    }
    if (rule.occurrenceTo === null && index !== sorted.length - 1) {
      return 'Only the last late arrival rule can apply to every later occurrence';
    }
  }
  return null;
}

export function ordinal(value: number): string {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
  switch (value % 10) {
    case 1:
      return `${value}st`;
    case 2:
      return `${value}nd`;
    case 3:
      return `${value}rd`;
    default:
      return `${value}th`;
  }
}

export function occurrenceRangeLabel(rule: RuleRange): string {
  if (rule.occurrenceTo === null) {
    return `${ordinal(rule.occurrenceFrom)} and later`;
  }
  if (rule.occurrenceTo === rule.occurrenceFrom) {
    return ordinal(rule.occurrenceFrom);
  }
  return `${ordinal(rule.occurrenceFrom)} to ${ordinal(rule.occurrenceTo)}`;
}

export function formatLeaveDays(days: number): string {
  const rounded = roundDays(days);
  return `${rounded} day${rounded === 1 ? '' : 's'}`;
}
